// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type ReportVersionStatus = "draft" | "approved" | "superseded";
export const ReportVersionStatusSchema = z.enum(["draft", "approved", "superseded"] as const);

export type ReportArtifactState = "none" | "running" | "failed" | "ready" | "expired";
export const ReportArtifactStateSchema = z.enum(["none", "running", "failed", "ready", "expired"] as const);

export type ReportAssemblyState = "none" | "queued" | "running" | "failed" | "ready";
export const ReportAssemblyStateSchema = z.enum(["none", "queued", "running", "failed", "ready"] as const);

export type ReportVersion = {
  version: number;
  title: string;
  summary: string;
  introduction?: string | null | undefined;
  conclusion?: string | null | undefined;
  status: ReportVersionStatus;
  generatedAt: string | null;
  generatedBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  sourceRevision: string | null;
  contentHash: string | null;
  artifact: ReportArtifactState;
  expiresAt: string | null;
  watermark: boolean;
  assembly: ReportAssemblyState;
  assemblyError: string | null;
  assemblyKey: string | null;
  answerRevision: number | null;
  assembledAt: string | null;
  assemblyContentHash: string | null;
  evidenceCount: number;
  unresolvedAiFindingIds: Array<string>;
};
export const ReportVersionSchema = z.object({
  version: z.number().int(),
  title: z.string(),
  summary: z.string(),
  introduction: z.string().nullable().optional(),
  conclusion: z.string().nullable().optional(),
  status: ReportVersionStatusSchema,
  generatedAt: z.string().nullable(),
  generatedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  sourceRevision: z.string().nullable(),
  contentHash: z.string().nullable(),
  artifact: ReportArtifactStateSchema,
  expiresAt: z.string().nullable(),
  watermark: z.boolean(),
  assembly: ReportAssemblyStateSchema,
  assemblyError: z.string().nullable(),
  assemblyKey: z.string().nullable(),
  answerRevision: z.number().int().nullable(),
  assembledAt: z.string().nullable(),
  assemblyContentHash: z.string().nullable(),
  evidenceCount: z.number().int(),
  unresolvedAiFindingIds: z.array(z.string()),
}).strict();

export type ReportListItem = {
  _id: string;
  organizationId: string;
  inspectionId: string;
  subjectId: string | null;
  subjectName: string | null;
  domainKey: string | null;
  typeName: string | null;
  inspectionStatus: string | null;
  currentVersion: number;
  status: ReportVersionStatus;
  artifact: ReportArtifactState;
  generatedAt: string | null;
  expiresAt: string | null;
  updatedAt: string;
};
export const ReportListItemSchema = z.object({
  _id: z.string(),
  organizationId: z.string(),
  inspectionId: z.string(),
  subjectId: z.string().nullable(),
  subjectName: z.string().nullable(),
  domainKey: z.string().nullable(),
  typeName: z.string().nullable(),
  inspectionStatus: z.string().nullable(),
  currentVersion: z.number().int(),
  status: ReportVersionStatusSchema,
  artifact: ReportArtifactStateSchema,
  generatedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  updatedAt: z.string(),
}).strict();

export type ReportEvidencePhotoItem = {
  thumbnailUrl: string | null;
  caption: string | null;
};
export const ReportEvidencePhotoItemSchema = z.object({
  thumbnailUrl: z.string().nullable(),
  caption: z.string().nullable(),
}).strict();

export type ReportChip = {
  label: string;
  color: string | null;
};
export const ReportChipSchema = z.object({
  label: z.string(),
  color: z.string().nullable(),
}).strict();

export type ReportContentRow = {
  label: string;
  value: string;
  tone: "neutral" | "positive" | "warning" | "critical";
  kind: "text" | "photo-grid" | "chip";
  photos: Array<ReportEvidencePhotoItem> | null;
  chip: ReportChip | null;
  description: string | null;
};
export const ReportContentRowSchema = z.object({
  label: z.string(),
  value: z.string(),
  tone: z.enum(["neutral", "positive", "warning", "critical"] as const).default("neutral"),
  kind: z.enum(["text", "photo-grid", "chip"] as const).default("text"),
  photos: z.array(ReportEvidencePhotoItemSchema).nullable(),
  chip: ReportChipSchema.nullable(),
  description: z.string().nullable(),
}).strict();

export type ReportTableCell = {
  kind: "text" | "photo-grid" | "chip";
  value: string;
  photos: Array<ReportEvidencePhotoItem> | null;
  chip: ReportChip | null;
};
export const ReportTableCellSchema = z.object({
  kind: z.enum(["text", "photo-grid", "chip"] as const).default("text"),
  value: z.string(),
  photos: z.array(ReportEvidencePhotoItemSchema).nullable(),
  chip: ReportChipSchema.nullable(),
}).strict();

export type ReportContentSection = {
  key: string;
  heading: string;
  kind: "section" | "page" | "table";
  pageBreak: "auto" | "always" | "avoid";
  presentation: {
  layout: "list" | "groupedList" | "table" | "summaryCards" | "narrative";
  columns: "one" | "two";
  density: "comfortable" | "compact" | "condensed";
  heading: "visible" | "hidden" | "renamed" | "numbered";
  headingLabel: string | null;
  repeatHeading: boolean;
  showEmpty: "show" | "hide" | "notProvided";
  evidencePlacement: "inline" | "afterResponse" | "gallery" | "appendix";
  findingEmphasis: "standard" | "highlighted" | "summaryOnly";
  tableColumns: Array<{
  key: string;
  visible: boolean;
  emphasis: "standard" | "primary" | "secondary" | "compact";
}> | null;
  overflow: "wrap" | "truncate";
  showTotalsRow: boolean;
};
  tocKey: string | null;
  body: string | null;
  rows: Array<ReportContentRow>;
  columns: Array<string> | null;
  columnKeys: Array<string> | null;
  tableRows: Array<Array<ReportTableCell>> | null;
};
export const ReportContentSectionSchema = z.object({
  key: z.string(),
  heading: z.string(),
  kind: z.enum(["section", "page", "table"] as const).default("section"),
  pageBreak: z.enum(["auto", "always", "avoid"] as const).default("auto"),
  presentation: z.object({
    layout: z.enum(["list", "groupedList", "table", "summaryCards", "narrative"] as const).default("list"),
    columns: z.enum(["one", "two"] as const).default("one"),
    density: z.enum(["comfortable", "compact", "condensed"] as const).default("comfortable"),
    heading: z.enum(["visible", "hidden", "renamed", "numbered"] as const).default("visible"),
    headingLabel: z.string().nullable().default(null),
    repeatHeading: z.boolean().default(false),
    showEmpty: z.enum(["show", "hide", "notProvided"] as const).default("show"),
    evidencePlacement: z.enum(["inline", "afterResponse", "gallery", "appendix"] as const).default("inline"),
    findingEmphasis: z.enum(["standard", "highlighted", "summaryOnly"] as const).default("standard"),
    tableColumns: z.array(z.object({
      key: z.string(),
      visible: z.boolean().default(true),
      emphasis: z.enum(["standard", "primary", "secondary", "compact"] as const).default("standard"),
    }).strict()).nullable().default(null),
    overflow: z.enum(["wrap", "truncate"] as const).default("wrap"),
    showTotalsRow: z.boolean().default(false),
  }).strict(),
  tocKey: z.string().nullable(),
  body: z.string().nullable(),
  rows: z.array(ReportContentRowSchema),
  columns: z.array(z.string()).nullable(),
  columnKeys: z.array(z.string()).nullable(),
  tableRows: z.array(z.array(ReportTableCellSchema)).nullable(),
}).strict();

export type ReportBranding = {
  organizationName: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  font: "outfit" | "system" | "serif" | null;
};
export const ReportBrandingSchema = z.object({
  organizationName: z.string().nullable(),
  logoUrl: z.string().nullable(),
  primaryColor: z.string().nullable(),
  accentColor: z.string().nullable(),
  font: z.enum(["outfit", "system", "serif"] as const).nullable(),
}).strict();

export type ReportLetterheadSlot = {
  enabled: boolean;
  content: string | null;
};
export const ReportLetterheadSlotSchema = z.object({
  enabled: z.boolean().default(false),
  content: z.string().nullable().default(null),
}).strict();

export type ReportLetterhead = {
  logoLeft: ReportLetterheadSlot;
  logoCenter: ReportLetterheadSlot;
  logoRight: ReportLetterheadSlot;
  title: ReportLetterheadSlot;
  addressBlock: ReportLetterheadSlot;
  footerPageNumber: ReportLetterheadSlot;
  footerText: ReportLetterheadSlot;
  mode: "none" | "firstContentPage" | "everyContentPage";
  layout: "standard" | "compact";
  confidentiality: "none" | "confidential" | "internal" | "restricted";
  textColor: string | null;
};
export const ReportLetterheadSchema = z.object({
  logoLeft: ReportLetterheadSlotSchema,
  logoCenter: ReportLetterheadSlotSchema,
  logoRight: ReportLetterheadSlotSchema,
  title: ReportLetterheadSlotSchema,
  addressBlock: ReportLetterheadSlotSchema,
  footerPageNumber: ReportLetterheadSlotSchema,
  footerText: ReportLetterheadSlotSchema,
  mode: z.enum(["none", "firstContentPage", "everyContentPage"] as const).default("firstContentPage"),
  layout: z.enum(["standard", "compact"] as const).default("standard"),
  confidentiality: z.enum(["none", "confidential", "internal", "restricted"] as const).default("none"),
  textColor: z.string().nullable().default(null),
}).strict();

export type ReportLayoutPolicy = {
  pageFormat: "A4" | "letter";
  orientation: "portrait" | "landscape";
  marginsMm: {
  top: number;
  right: number;
  bottom: number;
  left: number;
};
  frontMatter: {
  enabled: boolean;
  countsTowardPageNumbers: boolean;
};
  letterhead: {
  mode: "none" | "firstContentPage" | "everyContentPage";
};
};
export const ReportLayoutPolicySchema = z.object({
  pageFormat: z.enum(["A4", "letter"] as const),
  orientation: z.enum(["portrait", "landscape"] as const),
  marginsMm: z.object({
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    left: z.number(),
  }).strict(),
  frontMatter: z.object({
    enabled: z.boolean(),
    countsTowardPageNumbers: z.boolean(),
  }).strict(),
  letterhead: z.object({
    mode: z.enum(["none", "firstContentPage", "everyContentPage"] as const),
  }).strict(),
}).strict();

export type ReportStyle = {
  logo: string | null;
  accentColor: string | null;
  font: "sans" | "serif" | "mono";
  sectionHeaderStyle: "filled" | "centered" | "minimal";
  thumbnailSize: "small" | "medium" | "large";
  pageSize: "A4" | "letter";
  coverPage: boolean;
  headingScale: "sm" | "md" | "lg";
  secondaryTextFromAccent: boolean;
  sectionUnderlineWidth: "full" | "text";
  surfaceRadius: "rounded" | "sharp";
  pageOrientation: "portrait" | "landscape";
  marginsPreset: "narrow" | "normal" | "wide";
  letterhead: ReportLetterhead;
  typographyRoles: {
  caption: "sm" | "md";
};
  colorRoles: {
  brand: "accent" | "neutral";
  heading: "default" | "brand";
  border: "default" | "brand";
  findingSeverity: "standard" | "brand";
};
  borderDensity: "standard" | "bold";
  variants: {
  cover: "standard" | "accent";
  letterhead: "standard" | "divider";
  table: "standard" | "striped";
  finding: "standard" | "compact";
};
};
export const ReportStyleSchema = z.object({
  logo: z.string().nullable(),
  accentColor: z.string().nullable(),
  font: z.enum(["sans", "serif", "mono"] as const).default("sans"),
  sectionHeaderStyle: z.enum(["filled", "centered", "minimal"] as const).default("filled"),
  thumbnailSize: z.enum(["small", "medium", "large"] as const).default("medium"),
  pageSize: z.enum(["A4", "letter"] as const).default("A4"),
  coverPage: z.boolean().default(false),
  headingScale: z.enum(["sm", "md", "lg"] as const).default("md"),
  secondaryTextFromAccent: z.boolean().default(false),
  sectionUnderlineWidth: z.enum(["full", "text"] as const).default("full"),
  surfaceRadius: z.enum(["rounded", "sharp"] as const).default("rounded"),
  pageOrientation: z.enum(["portrait", "landscape"] as const).default("portrait"),
  marginsPreset: z.enum(["narrow", "normal", "wide"] as const).default("normal"),
  letterhead: ReportLetterheadSchema,
  typographyRoles: z.object({
    caption: z.enum(["sm", "md"] as const).default("sm"),
  }).strict().default({"caption":"sm"}),
  colorRoles: z.object({
    brand: z.enum(["accent", "neutral"] as const).default("accent"),
    heading: z.enum(["default", "brand"] as const).default("default"),
    border: z.enum(["default", "brand"] as const).default("default"),
    findingSeverity: z.enum(["standard", "brand"] as const).default("standard"),
  }).strict().default({"brand":"accent","heading":"default","border":"default","findingSeverity":"standard"}),
  borderDensity: z.enum(["standard", "bold"] as const).default("standard"),
  variants: z.object({
    cover: z.enum(["standard", "accent"] as const).default("standard"),
    letterhead: z.enum(["standard", "divider"] as const).default("standard"),
    table: z.enum(["standard", "striped"] as const).default("standard"),
    finding: z.enum(["standard", "compact"] as const).default("standard"),
  }).strict().default({"cover":"standard","letterhead":"standard","table":"standard","finding":"standard"}),
}).strict();

export type ReportScoreSummary = {
  earned: number;
  possible: number;
  percentage: number | null;
};
export const ReportScoreSummarySchema = z.object({
  earned: z.number(),
  possible: z.number(),
  percentage: z.number().nullable(),
}).strict();

export type ReportFindingRollupItem = {
  observationText: string;
  recommendationText: string | null;
  severityTag: string | null;
  evidenceCount: number;
};
export const ReportFindingRollupItemSchema = z.object({
  observationText: z.string(),
  recommendationText: z.string().nullable(),
  severityTag: z.string().nullable(),
  evidenceCount: z.number().int(),
}).strict();

export type ReportFlaggedItemsRollup = {
  total: number;
  bySeverity: {
  low: number;
  medium: number;
  high: number;
  critical: number;
};
  items: Array<ReportFindingRollupItem>;
};
export const ReportFlaggedItemsRollupSchema = z.object({
  total: z.number().int(),
  bySeverity: z.object({
    low: z.number().int(),
    medium: z.number().int(),
    high: z.number().int(),
    critical: z.number().int(),
  }).strict(),
  items: z.array(ReportFindingRollupItemSchema),
}).strict();

export type ReportActionItem = {
  observationText: string;
  recommendationText: string;
  severityTag: string | null;
};
export const ReportActionItemSchema = z.object({
  observationText: z.string(),
  recommendationText: z.string(),
  severityTag: z.string().nullable(),
}).strict();

export type ReportActionsRollup = {
  total: number;
  items: Array<ReportActionItem>;
};
export const ReportActionsRollupSchema = z.object({
  total: z.number().int(),
  items: z.array(ReportActionItemSchema),
}).strict();

export type ReportTocEntry = {
  key: string;
  label: string;
  page?: number | null | undefined;
};
export const ReportTocEntrySchema = z.object({
  key: z.string(),
  label: z.string(),
  page: z.number().int().nullable().optional(),
}).strict();

export type ReportMediaPhoto = {
  roomName: string | null;
  thumbnailUrl: string | null;
  caption: string | null;
  fieldLabel: string | null;
  sectionName: string | null;
  capturedAt: string | null;
};
export const ReportMediaPhotoSchema = z.object({
  roomName: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  caption: z.string().nullable(),
  fieldLabel: z.string().nullable(),
  sectionName: z.string().nullable(),
  capturedAt: z.string().nullable(),
}).strict();

export type ReportMediaSummary = {
  totalPhotos: number;
  roomsWithPhotos: number;
  photos: Array<ReportMediaPhoto>;
};
export const ReportMediaSummarySchema = z.object({
  totalPhotos: z.number().int(),
  roomsWithPhotos: z.number().int(),
  photos: z.array(ReportMediaPhotoSchema),
}).strict();

export type ReportMediaSummaryPresentation = {
  layout: "grid" | "list" | "fullWidth" | "appendix";
  imageFit: "cover" | "contain";
  showCaptions: boolean;
  showTimestamps: boolean;
  showLocation: boolean;
  showAnnotations: boolean;
  groupBy: "none" | "question" | "section" | "finding";
};
export const ReportMediaSummaryPresentationSchema = z.object({
  layout: z.enum(["grid", "list", "fullWidth", "appendix"] as const).default("grid"),
  imageFit: z.enum(["cover", "contain"] as const).default("cover"),
  showCaptions: z.boolean().default(true),
  showTimestamps: z.boolean().default(false),
  showLocation: z.boolean().default(false),
  showAnnotations: z.boolean().default(false),
  groupBy: z.enum(["none", "question", "section", "finding"] as const).default("none"),
}).strict();

export type ReportContentToggles = {
  footer: boolean;
  tableOfContents: boolean;
  pageBreak: boolean;
  summary: boolean;
  flaggedItems: boolean;
  actions: boolean;
  disclaimer: boolean;
  inspectionResults: boolean;
  tables: boolean;
  mediaSummary: boolean;
  scores: boolean;
  methodology: boolean;
  showQuestionLabels: boolean;
  showAnswers: boolean;
  showDescriptions: boolean;
  metadata: boolean;
  includeInternalNotes: boolean;
  evidenceCaptions: boolean;
};
export const ReportContentTogglesSchema = z.object({
  footer: z.boolean().default(true),
  tableOfContents: z.boolean().default(false),
  pageBreak: z.boolean().default(true),
  summary: z.boolean().default(false),
  flaggedItems: z.boolean().default(true),
  actions: z.boolean().default(true),
  disclaimer: z.boolean().default(false),
  inspectionResults: z.boolean().default(true),
  tables: z.boolean().default(true),
  mediaSummary: z.boolean().default(true),
  scores: z.boolean().default(true),
  methodology: z.boolean().default(false),
  showQuestionLabels: z.boolean().default(true),
  showAnswers: z.boolean().default(true),
  showDescriptions: z.boolean().default(false),
  metadata: z.boolean().default(false),
  includeInternalNotes: z.boolean().default(false),
  evidenceCaptions: z.boolean().default(false),
}).strict();

export type ReportFindingsGroupBy = "severity" | "section" | "status" | "assignee";
export const ReportFindingsGroupBySchema = z.enum(["severity", "section", "status", "assignee"] as const);

export type ReportContent = {
  title: string;
  accentColor: string | null;
  headerStyle: "standard" | "centered" | "minimal";
  style: ReportStyle;
  layoutPolicy: ReportLayoutPolicy;
  templateName: string | null;
  templateVersion: number | null;
  subjectName: string | null;
  subjectAddress: string | null;
  typeName: string | null;
  inspectionStatus: string | null;
  inspectionDate: string | null;
  generatedAt: string | null;
  reportReference: string | null;
  branding: ReportBranding | null;
  sections: Array<ReportContentSection>;
  scoreSummary: ReportScoreSummary | null;
  flaggedItems: ReportFlaggedItemsRollup | null;
  actions: ReportActionsRollup | null;
  tableOfContents: Array<ReportTocEntry>;
  disclaimer: string | null;
  methodology: string | null;
  mediaSummary: ReportMediaSummary | null;
  mediaSummaryPresentation: ReportMediaSummaryPresentation;
  contentToggles: ReportContentToggles;
  findingsGroupBy: ReportFindingsGroupBy;
};
export const ReportContentSchema = z.object({
  title: z.string(),
  accentColor: z.string().nullable(),
  headerStyle: z.enum(["standard", "centered", "minimal"] as const).default("standard"),
  style: ReportStyleSchema,
  layoutPolicy: ReportLayoutPolicySchema,
  templateName: z.string().nullable(),
  templateVersion: z.number().int().nullable(),
  subjectName: z.string().nullable(),
  subjectAddress: z.string().nullable(),
  typeName: z.string().nullable(),
  inspectionStatus: z.string().nullable(),
  inspectionDate: z.string().nullable(),
  generatedAt: z.string().nullable(),
  reportReference: z.string().nullable(),
  branding: ReportBrandingSchema.nullable(),
  sections: z.array(ReportContentSectionSchema),
  scoreSummary: ReportScoreSummarySchema.nullable(),
  flaggedItems: ReportFlaggedItemsRollupSchema.nullable(),
  actions: ReportActionsRollupSchema.nullable(),
  tableOfContents: z.array(ReportTocEntrySchema),
  disclaimer: z.string().nullable(),
  methodology: z.string().nullable(),
  mediaSummary: ReportMediaSummarySchema.nullable(),
  mediaSummaryPresentation: ReportMediaSummaryPresentationSchema,
  contentToggles: ReportContentTogglesSchema,
  findingsGroupBy: ReportFindingsGroupBySchema,
}).strict();

export type ReportDetail = {
  _id: string;
  organizationId: string;
  inspectionId: string;
  subjectId: string | null;
  subjectName: string | null;
  subjectAddress: string | null;
  domainKey: string | null;
  typeName: string | null;
  inspectionStatus: string | null;
  currentVersion: number;
  versions: Array<ReportVersion>;
  selectedVersion: number;
  content: ReportContent | null;
  approvalChain: Array<{
  step: number;
  name: string;
  permission: string;
  requireDistinctFrom: Array<"preparer">;
  preparerId: string | null;
  satisfiedBy: string | null;
  satisfiedAt: string | null;
}>;
  createdAt: string;
  updatedAt: string;
};
export const ReportDetailSchema = z.object({
  _id: z.string(),
  organizationId: z.string(),
  inspectionId: z.string(),
  subjectId: z.string().nullable(),
  subjectName: z.string().nullable(),
  subjectAddress: z.string().nullable(),
  domainKey: z.string().nullable(),
  typeName: z.string().nullable(),
  inspectionStatus: z.string().nullable(),
  currentVersion: z.number().int(),
  versions: z.array(ReportVersionSchema),
  selectedVersion: z.number().int(),
  content: ReportContentSchema.nullable(),
  approvalChain: z.array(z.object({
    step: z.number().int().min(1),
    name: z.string(),
    permission: z.string(),
    requireDistinctFrom: z.array(z.enum(["preparer"] as const)),
    preparerId: z.string().nullable(),
    satisfiedBy: z.string().nullable(),
    satisfiedAt: z.string().nullable(),
  }).strict()),
  createdAt: z.string(),
  updatedAt: z.string(),
}).strict();

export type DownloadReportResult = {
  downloadUrl: string;
  version: number;
  filename: string;
  generatedAt: string;
  expiresAt: string;
  expiryMessage: string;
};
export const DownloadReportResultSchema = z.object({
  downloadUrl: z.string(),
  version: z.number().int(),
  filename: z.string(),
  generatedAt: z.string(),
  expiresAt: z.string(),
  expiryMessage: z.string(),
}).strict();

export type RegenerateReportBody = {
  version?: number | undefined;
  force?: boolean | undefined;
};
export const RegenerateReportBodySchema = z.object({
  version: z.number().int().min(1).optional(),
  force: z.boolean().default(false),
}).strict();

export type RegenerateReportResult = {
  reportId: string;
  version: number;
  status: ReportVersionStatus;
  artifact: ReportArtifactState;
  idempotent: boolean;
  sourceRevision: string | null;
  downloadUrl: string | null;
  generatedAt: string | null;
  expiresAt: string | null;
  message: string;
};
export const RegenerateReportResultSchema = z.object({
  reportId: z.string(),
  version: z.number().int(),
  status: ReportVersionStatusSchema,
  artifact: ReportArtifactStateSchema,
  idempotent: z.boolean(),
  sourceRevision: z.string().nullable(),
  downloadUrl: z.string().nullable(),
  generatedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  message: z.string(),
}).strict();

export type ListReportsResponse = {
  items: Array<ReportListItem>;
  nextCursor: string | null;
  total?: number | undefined;
};

export const ListReportsQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
  q: z.string().min(1).optional(),
  status: z.enum(["draft", "approved", "superseded", "all"] as const).default("all"),
  artifact: z.enum(["ready", "expired", "none", "all"] as const).default("all"),
  inspectionId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
});

export async function listReports(query?: {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  q?: string | undefined;
  status?: "draft" | "approved" | "superseded" | "all" | undefined;
  artifact?: "ready" | "expired" | "none" | "all" | undefined;
  inspectionId?: string | undefined;
}): Promise<ListReportsResponse> {
  const response = await api.get<ListReportsResponse>(`/reports`, { params: query });
  return response.data;
}

export type GetReportResponse = {
  data: ReportDetail;
};

export const GetReportParamsSchema = z.object({
  reportId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export const GetReportQuerySchema = z.object({
  version: z.coerce.number().int().min(1).optional(),
}).strict();

export async function getReport(reportId: string, query?: {
  version?: number | undefined;
}): Promise<GetReportResponse> {
  const response = await api.get<GetReportResponse>(`/reports/${encodeURIComponent(reportId)}`, { params: query });
  return response.data;
}

export type DownloadReportResponse = {
  data: DownloadReportResult;
};

export const DownloadReportParamsSchema = z.object({
  reportId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export const DownloadReportQuerySchema = z.object({
  version: z.coerce.number().int().min(1).optional(),
}).strict();

export async function downloadReport(reportId: string, query?: {
  version?: number | undefined;
}): Promise<DownloadReportResponse> {
  const response = await api.get<DownloadReportResponse>(`/reports/${encodeURIComponent(reportId)}/download`, { params: query });
  return response.data;
}

export type RegenerateReportResponse = {
  data: RegenerateReportResult;
};

export const RegenerateReportParamsSchema = z.object({
  reportId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function regenerateReport(reportId: string, body: RegenerateReportBody): Promise<RegenerateReportResponse> {
  const response = await api.post<RegenerateReportResponse>(`/reports/${encodeURIComponent(reportId)}/generate-pdf`, body);
  return response.data;
}

export const reportsKeys = {
  all: ["reports"] as const,
  lists: () => [...reportsKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...reportsKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...reportsKeys.all, 'detail'] as const,
  detail: (id: string) => [...reportsKeys.details(), id] as const,
};
